/**
 * iter78: Database export/import UI inside Settings page.
 *
 * Admin-only feature. Two flows:
 *   - Export: shows stats, lets admin download a ZIP snapshot (JSON + files)
 *   - Import: file picker + mode toggle (merge | replace) + confirmation
 */
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import {
  Database,
  Download,
  Upload,
  AlertTriangle,
  Loader2,
  CheckCircle2,
  FileWarning,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const COLLECTION_LABELS = {
  users: "Usuários",
  jobs: "Vagas",
  candidates: "Candidatos",
  settings: "Configurações",
  organizations: "Organizações",
  departments: "Departamentos",
  candidate_statuses: "Status customizados",
  candidate_notes: "Notas de candidatos",
  candidate_comparisons: "Comparações",
  profiles: "Perfis de acesso",
  messages_log: "Histórico de mensagens",
  interviews: "Entrevistas",
  manager_availability: "Agenda de gestores",
  whatsapp_flows: "Fluxos IA",
  assessments: "Testes",
  app_notifications: "Notificações",
  notification_prefs: "Preferências de notificação",
  linkedin_accounts: "Contas LinkedIn",
  linkedin_auth_intents: "Conexões LinkedIn pendentes",
  linkedin_leads: "Leads LinkedIn",
  linkedin_threads: "Conversas LinkedIn",
  linkedin_messages: "Mensagens LinkedIn",
  linkedin_sourcing_saved_searches: "Buscas salvas LinkedIn",
};

const formatBytes = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const DatabasePortabilityPanel = () => {
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [importMode, setImportMode] = useState("merge");
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [importing, setImporting] = useState(false);
  const [lastImportResult, setLastImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const res = await axios.get(`${API}/settings/database/export-stats`);
      setStats(res.data);
    } catch (err) {
      if (err?.response?.status === 403) {
        setStats({ forbidden: true });
      } else {
        toast.error("Não foi possível carregar as estatísticas do banco.");
      }
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  // Download the JSON export — uses fetch+blob to trigger browser download
  const handleDownload = async () => {
    if (!window.confirm(
      `Você está prestes a baixar ${stats?.total_documents || 0} documentos `
      + `(~${formatBytes(stats?.estimated_bytes || 0)}). O arquivo conterá `
      + "dados sensíveis (incluindo configurações e contatos de candidatos). "
      + "Guarde em local seguro. Continuar?",
    )) return;

    setDownloading(true);
    try {
      const token = localStorage.getItem("rh_token") || "";
      const res = await fetch(`${API}/settings/database/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      // Extract filename from Content-Disposition or default
      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match
        ? match[1]
        : `gridrecruter-export-${new Date().toISOString().slice(0,10)}.zip`;
      // Trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Backup baixado: ${filename}`);
    } catch (err) {
      toast.error(`Falha ao exportar: ${err.message}`);
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) {
      toast.error("Selecione um arquivo .zip válido.");
      return;
    }
    if (f.size > 500 * 1024 * 1024) {
      toast.error("Arquivo muito grande (>500 MB).");
      return;
    }
    setSelectedFile(f);
    setLastImportResult(null);
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error("Selecione um arquivo .zip primeiro.");
      return;
    }
    if (importMode === "replace" && !confirmReplace) {
      toast.error('Marque "Confirmo a exclusão" para modo Substituir.');
      return;
    }
    const fileLabel = selectedFile?.name || "backup.zip";
    const confirmMsg = importMode === "replace"
      ? "⚠️ ATENÇÃO: o modo SUBSTITUIR vai DELETAR todos os seus dados atuais "
        + "antes de importar o backup. Esta ação é IRREVERSÍVEL. "
        + "Confirma que deseja prosseguir?"
      : `Importar ${fileLabel} no modo MESCLAR? Documentos existentes `
        + "serão atualizados, novos serão criados, nada será deletado.";
    if (!window.confirm(confirmMsg)) return;

    setImporting(true);
    setLastImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("mode", importMode);
      formData.append("confirm_replace", confirmReplace ? "true" : "false");
      const res = await axios.post(
        `${API}/settings/database/import`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setLastImportResult(res.data);
      const filesMsg = res.data.total_files_restored
        ? ` + ${res.data.total_files_restored} arquivos restaurados.`
        : "";
      toast.success(
        `Importado com sucesso! ${res.data.total_inserted} novos + ${res.data.total_updated} atualizados.${filesMsg}`,
      );
      // Reload stats so the user sees the updated counts
      loadStats();
      // Reset the form
      setSelectedFile(null);
      setConfirmReplace(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Falha ao importar o arquivo.",
      );
    } finally {
      setImporting(false);
    }
  };

  if (loadingStats) {
    return (
      <div className="flex items-center gap-2 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        Carregando estatísticas do banco…
      </div>
    );
  }

  if (stats?.forbidden) {
    return (
      <div
        data-testid="database-panel-forbidden"
        className="bg-amber-50 border border-amber-200 rounded-lg p-5 text-sm text-amber-900"
      >
        <AlertTriangle className="w-5 h-5 inline mr-2" />
        Apenas administradores podem acessar a exportação/importação do banco
        de dados. Solicite a um administrador da sua empresa.
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="database-portability-panel">
      {/* Export section */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="bg-blue-50 rounded-lg p-2">
            <Download className="w-5 h-5 text-brand-blue" />
          </div>
          <div className="flex-1">
            <h3 className="heading text-base font-semibold text-slate-900 mb-1">
              Exportar banco de dados
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Baixe um snapshot completo dos dados da sua empresa em ZIP
              (JSON + currículos PDF + logos). Use pra{" "}
              <strong>backup antes de mudanças críticas</strong> ou pra
              migrar entre ambientes (dev → produção, por exemplo).
              Senhas e chaves criptografadas são preservadas — você pode
              importar de volta sem perder integrações.
            </p>
          </div>
        </div>

        {stats && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                Conteúdo do backup
              </span>
              <span className="text-xs text-slate-500">
                <strong className="text-slate-900">{stats.total_documents}</strong>{" "}
                documentos · ~<strong className="text-slate-900">{formatBytes(stats.estimated_bytes)}</strong>
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 text-xs">
              {Object.entries(stats.stats || {})
                .filter(([_, count]) => count > 0)
                .sort((a, b) => b[1] - a[1])
                .map(([key, count]) => (
                  <div key={key} className="flex justify-between gap-2">
                    <span className="text-slate-600 truncate">
                      {COLLECTION_LABELS[key] || key}
                    </span>
                    <span className="font-semibold text-slate-900">{count}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        <Button
          type="button"
          onClick={handleDownload}
          disabled={downloading || !stats?.total_documents}
          data-testid="db-export-btn"
          className="bg-brand-blue hover:bg-brand-blue/90"
        >
          {downloading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Gerando arquivo…
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Baixar backup (.zip)
            </>
          )}
        </Button>
      </div>

      {/* Import section */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="bg-emerald-50 rounded-lg p-2">
            <Upload className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h3 className="heading text-base font-semibold text-slate-900 mb-1">
              Importar banco de dados
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Restaure um backup feito anteriormente. Escolha o modo com cuidado:
              <strong className="text-slate-900"> Mesclar</strong> é seguro
              (apenas adiciona/atualiza); <strong className="text-rose-700"> Substituir</strong>{" "}
              <em>apaga todos os dados</em> da sua empresa antes de importar.
            </p>
          </div>
        </div>

        <div>
          <Label className="text-xs font-semibold">Arquivo de backup</Label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip,application/zip"
            onChange={handleFileSelect}
            data-testid="db-import-file-input"
            className="mt-1 block w-full text-sm text-slate-700
              file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0
              file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200
              file:cursor-pointer"
          />
          {selectedFile && (
            <p className="text-xs text-slate-600 mt-1.5 flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
              <strong>{selectedFile.name}</strong> ({formatBytes(selectedFile.size)})
            </p>
          )}
        </div>

        <div>
          <Label className="text-xs font-semibold">Modo de importação</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            <label className={`border rounded-lg p-3 cursor-pointer transition-colors ${
              importMode === "merge"
                ? "border-emerald-300 bg-emerald-50"
                : "border-slate-200 hover:border-slate-300"
            }`}>
              <div className="flex items-start gap-2">
                <input
                  type="radio"
                  name="import-mode"
                  value="merge"
                  checked={importMode === "merge"}
                  onChange={() => { setImportMode("merge"); setConfirmReplace(false); }}
                  data-testid="db-import-mode-merge"
                  className="mt-1"
                />
                <div>
                  <div className="text-sm font-semibold text-emerald-900">
                    Mesclar (seguro)
                  </div>
                  <div className="text-[11px] text-slate-600 mt-0.5">
                    Adiciona novos documentos, atualiza existentes pelo ID.
                    Nada é deletado.
                  </div>
                </div>
              </div>
            </label>
            <label className={`border rounded-lg p-3 cursor-pointer transition-colors ${
              importMode === "replace"
                ? "border-rose-300 bg-rose-50"
                : "border-slate-200 hover:border-slate-300"
            }`}>
              <div className="flex items-start gap-2">
                <input
                  type="radio"
                  name="import-mode"
                  value="replace"
                  checked={importMode === "replace"}
                  onChange={() => setImportMode("replace")}
                  data-testid="db-import-mode-replace"
                  className="mt-1"
                />
                <div>
                  <div className="text-sm font-semibold text-rose-900 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Substituir (irreversível)
                  </div>
                  <div className="text-[11px] text-slate-600 mt-0.5">
                    DELETA todos os dados atuais antes de importar.
                    Use só pra restauração completa.
                  </div>
                </div>
              </div>
            </label>
          </div>
        </div>

        {importMode === "replace" && (
          <label className="flex items-start gap-2 text-xs text-rose-900 bg-rose-50 border border-rose-200 rounded p-3">
            <input
              type="checkbox"
              checked={confirmReplace}
              onChange={(e) => setConfirmReplace(e.target.checked)}
              data-testid="db-import-confirm-replace"
              className="mt-0.5"
            />
            <span>
              <strong>Confirmo a exclusão</strong> de todos os dados atuais da
              minha empresa. Esta ação não pode ser desfeita. Fiz backup antes
              de prosseguir.
            </span>
          </label>
        )}

        <Button
          type="button"
          onClick={handleImport}
          disabled={importing || !selectedFile || (importMode === "replace" && !confirmReplace)}
          data-testid="db-import-btn"
          className={importMode === "replace"
            ? "bg-rose-600 hover:bg-rose-700"
            : "bg-emerald-600 hover:bg-emerald-700"}
        >
          {importing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Importando…
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              {importMode === "replace" ? "Substituir banco" : "Mesclar dados"}
            </>
          )}
        </Button>

        {lastImportResult && (
          <div
            data-testid="db-import-result"
            className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs"
          >
            <div className="font-semibold text-emerald-900 flex items-center gap-1.5 mb-1.5">
              <CheckCircle2 className="w-4 h-4" />
              Importação concluída ({lastImportResult.mode})
            </div>
            <div className="text-slate-700">
              <strong>{lastImportResult.total_inserted}</strong> documentos
              inseridos / <strong>{lastImportResult.total_updated}</strong>{" "}
              atualizados em {Object.keys(lastImportResult.counters || {}).length}{" "}
              coleções.
              {lastImportResult.total_files_restored > 0 && (
                <>
                  <br />
                  <strong>{lastImportResult.total_files_restored}</strong>{" "}
                  arquivos restaurados (currículos, logos, capas).
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer warning */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex gap-2 text-[11px] text-slate-600">
        <FileWarning className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-400" />
        <span>
          O arquivo ZIP contém dados sensíveis (e-mails, telefones, currículos
          em PDF, chaves criptografadas de SMTP/WhatsApp/LinkedIn). Guarde
          em local seguro — qualquer pessoa com o arquivo pode importar pra
          uma instalação espelhada do GridRecruiter.
        </span>
      </div>
    </div>
  );
};

export default DatabasePortabilityPanel;
